import { useEffect } from 'react';

/**
 * Mantém a tela acesa enquanto houver partida aberta.
 *
 * O /controle fica minutos sem toque entre um sorteio e outro — tempo de
 * sobra para o celular apagar a tela. Quando isso acontece, o operador
 * precisa desbloquear o aparelho com o salão esperando a próxima bola.
 *
 * Três detalhes que a API exige e que não são óbvios:
 *
 * 1. **O lock morre quando a aba sai de foco** (atender uma ligação, trocar
 *    de app). O navegador não avisa de volta: é preciso **reaquisitar** no
 *    `visibilitychange`.
 * 2. **Só funciona em contexto seguro.** Em http na rede do salão a API
 *    simplesmente não existe — daí o fallback silencioso: sem suporte, sem
 *    erro, sem aviso. Não há o que a pessoa faça a respeito.
 * 3. `request()` **rejeita** se a página estiver escondida. Tratado como
 *    falha normal, não como exceção.
 */

interface WakeLockSentinelLike {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (type: 'release', listener: () => void) => void;
}

interface WakeLockApi {
  request: (type: 'screen') => Promise<WakeLockSentinelLike>;
}

function wakeLockApi(): WakeLockApi | null {
  if (typeof navigator === 'undefined') return null;
  return (navigator as { wakeLock?: WakeLockApi }).wakeLock ?? null;
}

export function wakeLockSupported(): boolean {
  return wakeLockApi() !== null;
}

/** Segura a tela acesa enquanto `active` for verdadeiro. */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    const api = wakeLockApi();
    if (!active || !api) return;

    let sentinel: WakeLockSentinelLike | null = null;
    let cancelled = false;

    const acquire = async () => {
      if (cancelled || document.visibilityState !== 'visible') return;

      try {
        sentinel = await api.request('screen');
        // O navegador pode soltar sozinho (bateria baixa, por exemplo).
        sentinel.addEventListener('release', () => {
          sentinel = null;
        });
      } catch {
        // Sem permissão, sem bateria, aba escondida: falha em silêncio.
        sentinel = null;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !sentinel) void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void sentinel?.release().catch(() => undefined);
      sentinel = null;
    };
  }, [active]);
}
